import { useCallback, useEffect, useRef, useState } from 'react'

import userApi from '../../../services/api/user'

// Retry helper function with exponential backoff
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      const isNetworkError =
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('Network request failed') ||
        error.name === 'TypeError' ||
        error.name === 'NetworkError'

      // Only retry on network errors, not on other errors (like 400, 401, etc.)
      if (!isNetworkError || attempt === maxRetries - 1) {
        throw error
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
}

const useNote = (subsidiaryId, kind = 'healthCheck-summary', { lazy = false, batchLoad = false } = {}) => {
  const [note, setNoteState] = useState('')
  const [isLoading, setIsLoading] = useState(!lazy && !batchLoad) // Don't load if lazy or batchLoad
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)
  const [hasLoaded, setHasLoaded] = useState(false) // Track if we've loaded
  const debounceTimerRef = useRef(null)
  const previousNoteRef = useRef('') // Store previous note to revert on error

  const key = `${kind}-${subsidiaryId}`

  // Load note function
  const loadNote = useCallback(async () => {
    if (!subsidiaryId) {
      setIsLoading(false)
      return
    }

    if (hasLoaded) {
      return // Already loaded
    }

    try {
      setIsLoading(true)
      setError(null)
      const response = await retryWithBackoff(() =>
        userApi.retrieveNote({
          key: key,
          kind: kind,
          subsidiaryId: subsidiaryId
        })
      )
      if (response?.data) {
        setNoteState(response.data)
        previousNoteRef.current = response.data
      } else {
        setNoteState('')
        previousNoteRef.current = ''
      }
      setHasLoaded(true)
    } catch (err) {
      console.error('Failed to load note after retries:', err)
      // Handle network errors more gracefully
      if (
        err.message?.includes('Failed to fetch') ||
        err.message?.includes('NetworkError') ||
        err.message?.includes('Network request failed')
      ) {
        setError('Unable to connect to server. Please check your connection.')
      } else {
        setError(err.message || 'Failed to load note')
      }
    } finally {
      setIsLoading(false)
    }
  }, [subsidiaryId, key, kind, hasLoaded])

  // Load note on mount (only if not lazy and not batchLoad)
  useEffect(() => {
    if (lazy || batchLoad) {
      setIsLoading(false)
      return
    }
    loadNote()
  }, [lazy, batchLoad, loadNote])

  // Function to load note on demand (for manual trigger or batch loading)
  const loadNoteOnDemand = useCallback(async () => {
    if (hasLoaded || isLoading) return
    await loadNote()
  }, [hasLoaded, isLoading, loadNote])

  // Expose loadNote for batch loading
  const loadNoteForBatch = useCallback(async () => {
    await loadNote()
  }, [loadNote])

  // Function to set note from external source (e.g., batch loading)
  const setNoteFromBatch = useCallback((newNote) => {
    setNoteState(newNote || '')
    previousNoteRef.current = newNote || ''
    setHasLoaded(true)
    setIsLoading(false)
  }, [])

  // Save note with debounce
  const saveNote = useCallback(
    async newNote => {
      if (!subsidiaryId) return

      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Store previous value from ref before optimistic update
      const previousNote = previousNoteRef.current

      // Optimistically update UI
      setNoteState(newNote)
      setError(null)

      // Debounce the API call (wait 500ms after last change)
      return new Promise((resolve, reject) => {
        debounceTimerRef.current = setTimeout(async () => {
          try {
            setIsSaving(true)
            setError(null)
            
            // Validate required parameters
            if (!subsidiaryId) {
              throw new Error('SubsidiaryId is required to save note')
            }
            
            await retryWithBackoff(() =>
              userApi.addOrUpdateNote({
                key: key,
                kind: kind,
                data: newNote,
                subsidiaryId: subsidiaryId
              })
            )
            
            // Update previous note ref on success
            previousNoteRef.current = newNote
            setIsSaving(false)
            resolve()
          } catch (err) {
            console.error('Failed to save note after retries:', err)
            console.error('Error details:', {
              message: err.message,
              stack: err.stack,
              key,
              kind,
              subsidiaryId
            })
            
            // Revert to previous value on persistent error
            setNoteState(previousNote)
            previousNoteRef.current = previousNote

            // Handle network errors more gracefully
            if (
              err.message?.includes('Failed to fetch') ||
              err.message?.includes('NetworkError') ||
              err.message?.includes('Network request failed') ||
              err.message?.includes('CORS') ||
              err.toString().includes('CORS')
            ) {
              setError('Unable to connect to server. CORS error - production API may not allow requests from localhost. Please check your connection or use the dev environment.')
            } else {
              setError(err.message || 'Failed to save note')
            }
            setIsSaving(false)
            reject(err)
          }
        }, 500)
      })
    },
    [key, kind, subsidiaryId]
  )

  return {
    note,
    setNote: saveNote,
    isLoading,
    isSaving,
    error,
    loadNoteOnDemand, // Expose for on-demand loading
    loadNoteForBatch, // Expose for batch loading
    setNoteFromBatch // Expose for batch-loaded notes
  }
}

export default useNote
